import { useMutation, useQuery } from '@tanstack/react-query'
import { Download } from 'lucide-react'
import { useState } from 'react'

import { Alert } from '@/components/feedback/Alert'
import { ErrorState } from '@/components/feedback/ErrorState'
import { Loading } from '@/components/feedback/Loading'
import { useToastStack } from '@/components/feedback/toast-stack'
import { OptionSelect } from '@/components/filters/OptionSelect'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { useCashFirm, useInvalidateCash } from '@/features/cash/hooks'
import { PaymentDecisionHistory, SendBackDialog } from '@/features/cash/PaymentDecisions'
import { CashFirmSelect } from '@/features/cash/shared'
import { HrPageHeader as PageHeader } from '@/features/hr/HrPageHeader'
import { useQueryState } from '@/hooks/use-query-state'
import { formatDateOnly, formatDateTime, formatDecimal } from '@/lib/format'
import { messages } from '@/lib/messages'
import { toProblem } from '@/lib/problem'
import { queryKeys } from '@/lib/query-keys'
import {
  cashApi,
  type CashPaymentList,
  type CashPaymentRow,
  type CashPaymentTransition,
} from '@/services/http/cash'

const t = messages.cash.paymentList
const INSTRUMENTS = ['banka', 'kasa', 'ciro', 'kendi_cek', 'kart'] as const
const SECTIONS = ['tedarikci', 'erp_disi', 'verilen_cek', 'kart', 'ciro_cek', 'tahsilat'] as const
const money = (v: number) => (Math.abs(v) < 0.005 ? '' : formatDecimal(v, 2))
const groupIban = (iban: string) => iban.replace(/(.{4})/g, '$1 ').trim()

function IbanCell({ firm, row, canEdit }: { firm: string; row: CashPaymentRow; canEdit: boolean }) {
  const info = row.iban!
  const [editing, setEditing] = useState(false)
  const [value, setValue] = useState('')
  const [error, setError] = useState<string | null>(null)
  const { toast } = useToastStack()
  const invalidate = useInvalidateCash()
  const save = useMutation({
    mutationFn: () => cashApi.saveIban(firm, row.cari_code!, value),
    onSuccess: () => {
      toast({ type: 'success', title: t.ibanSaved })
      setEditing(false)
      setError(null)
      void invalidate()
    },
    onError: (e) => setError(toProblem(e).detail ?? messages.cash.planning.failed),
  })
  const pending = info.action_status === 'bekliyor' || info.action_status === 'calisiyor'
  return (
    <div className="flex min-w-56 flex-col gap-1" data-testid="cash-iban-cell">
      {info.iban ? (
        <span className="font-mono text-xs whitespace-nowrap">{groupIban(info.iban)}</span>
      ) : (
        <Badge size="sm" tone="warning" data-iban="eksik">
          {t.ibanMissing}
        </Badge>
      )}
      <span className="flex flex-wrap gap-1">
        {pending && (
          <Badge size="sm" tone="info" data-iban="gonderiliyor">
            {t.ibanPending}
          </Badge>
        )}
        {info.action_status === 'tamam' && (
          <Badge size="sm" tone="success" data-iban="yazildi">
            {t.ibanDone}
          </Badge>
        )}
        {info.changed && (
          <Badge size="sm" tone="warning" data-iban="degisti">
            {t.ibanChanged}
          </Badge>
        )}
        {info.action_status === 'hata' && (
          <Badge size="sm" tone="danger" data-iban="hata" title={info.action_message ?? undefined}>
            {t.ibanError(info.action_message ?? '')}
          </Badge>
        )}
      </span>
      {canEdit && !pending && !editing && (
        <Button
          size="sm"
          variant="ghost"
          className="w-fit"
          onClick={() => setEditing(true)}
          aria-label={`${info.iban ? t.ibanEdit : t.ibanSave} ${row.name}`}
        >
          {info.iban ? t.ibanEdit : t.ibanSave}
        </Button>
      )}
      {editing && (
        <form
          className="flex flex-col gap-1"
          onSubmit={(e) => {
            e.preventDefault()
            save.mutate()
          }}
        >
          <Input
            className="h-8 font-mono text-xs"
            placeholder="TR.."
            value={value}
            onChange={(e) => setValue(e.target.value)}
            aria-label={t.ibanInput(row.name)}
            aria-invalid={!!error}
          />
          {error && (
            <span className="text-danger text-xs" role="alert">
              {error}
            </span>
          )}
          <span className="flex gap-1">
            <Button size="sm" type="submit" loading={save.isPending} disabled={!value.trim()}>
              {t.ibanSave}
            </Button>
            <Button size="sm" type="button" variant="ghost" onClick={() => setEditing(false)}>
              {messages.cash.planning.cancel}
            </Button>
          </span>
        </form>
      )}
    </div>
  )
}

function Section({
  firm,
  list,
  section,
  ibanFilter,
  canEdit,
}: {
  firm: string
  list: CashPaymentList
  section: (typeof SECTIONS)[number]
  ibanFilter: string | undefined
  canEdit: boolean
}) {
  let rows = list.sections[section] ?? []
  // IBAN yalnız bankadan ödemede gerekir (yapılacaklar sayısıyla aynı kural).
  if (section === 'tedarikci' && ibanFilter === 'eksik')
    rows = rows.filter((r) => r.iban?.missing && (r.amounts.banka ?? 0) > 0)
  if (section === 'tedarikci' && ibanFilter === 'hata')
    rows = rows.filter((r) => r.iban?.action_status === 'hata')
  const totals = Object.fromEntries(
    INSTRUMENTS.map((i) => [i, rows.reduce((s, r) => s + (r.amounts[i] ?? 0), 0)]),
  )
  const checks = list.checks.filter((c) => c.section === section)
  const bad = checks.filter((c) => !c.ok)
  const supplier = section === 'tedarikci'
  return (
    <section
      className="bg-surface flex flex-col gap-2 rounded-lg border p-3"
      aria-label={t.sections[section]}
      data-section={section}
    >
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <h2 className="text-sm font-semibold">{t.sections[section]}</h2>
        {checks.length > 0 && (
          <span
            className={bad.length ? 'text-danger text-xs' : 'text-success text-xs'}
            data-check={bad.length ? 'fark' : 'ayni'}
          >
            {bad.length
              ? bad
                  .map((c) =>
                    t.checkBad(`${c.instrument} ${formatDecimal(c.list_total - c.table_total)}`),
                  )
                  .join(' · ')
              : t.checkOk}
          </span>
        )}
      </div>
      {rows.length === 0 ? (
        <p className="text-text-secondary text-sm">{t.empty}</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-text-secondary border-b text-left text-xs">
                <th className="py-2 pr-3 font-medium">{t.date}</th>
                <th className="py-2 pr-3 font-medium">{t.name}</th>
                {supplier && <th className="py-2 pr-3 font-medium">{t.iban}</th>}
                {INSTRUMENTS.map((i) => (
                  <th key={i} className="py-2 pr-3 text-right font-medium">
                    {t.instruments[i]}
                  </th>
                ))}
                <th className="py-2 text-right font-medium">{t.total}</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.key} className="border-b align-top last:border-0" data-key={r.key}>
                  <td className="py-2 pr-3 whitespace-nowrap tabular-nums">
                    {r.date ? formatDateOnly(r.date) : ''}
                  </td>
                  <td className="py-2 pr-3">
                    {section === 'tahsilat' ? (t.collections[r.name] ?? r.name) : r.name}
                    <span className="text-text-secondary block text-xs">
                      {[r.cari_code, r.detail, r.group, r.overdue ? t.overdue : null]
                        .filter(Boolean)
                        .join(' · ')}
                    </span>
                  </td>
                  {supplier && (
                    <td className="py-2 pr-3">
                      {r.iban && <IbanCell firm={firm} row={r} canEdit={canEdit} />}
                    </td>
                  )}
                  {INSTRUMENTS.map((i) => (
                    <td key={i} className="py-2 pr-3 text-right tabular-nums">
                      {money(r.amounts[i] ?? 0)}
                    </td>
                  ))}
                  <td className="py-2 text-right font-medium tabular-nums">{money(r.total)}</td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr className="font-semibold">
                <td className="py-2 pr-3" colSpan={supplier ? 3 : 2}>
                  {t.total}
                </td>
                {INSTRUMENTS.map((i) => (
                  <td key={i} className="py-2 pr-3 text-right tabular-nums">
                    {money(totals[i]!)}
                  </td>
                ))}
                <td className="py-2 text-right tabular-nums" data-testid={`cash-total-${section}`}>
                  {money(Object.values(totals).reduce((a, b) => a + b, 0))}
                </td>
              </tr>
            </tfoot>
          </table>
        </div>
      )}
    </section>
  )
}

/** /nakit/odeme-listesi — haftanın ödeme listesi (docs/NAKIT-PLANI-PLAN.md Faz 4). */
export function PaymentListPage() {
  const { firms, firm, setFirm, can } = useCashFirm()
  const query = useQueryState()
  const week = query.get('hafta')
  const ibanFilter = query.get('iban')
  const canPlan = can('cash.plan')
  const canApprove = can('cash.approve')
  const { toast } = useToastStack()
  const invalidate = useInvalidateCash()
  const list = useQuery({
    queryKey: queryKeys.cash.paymentList(firm ?? '', week ?? ''),
    queryFn: () => cashApi.paymentList(firm!, week),
    enabled: !!firm,
  })
  const transition = useMutation({
    mutationFn: (body: CashPaymentTransition) => cashApi.transition(body),
    onSuccess: (l) => {
      toast({ type: 'success', title: t.transitioned(t.status[l.status] ?? l.status) })
      void invalidate()
    },
    onError: (e) =>
      toast({
        type: 'error',
        title: messages.cash.planning.failed,
        description: toProblem(e).detail ?? undefined,
      }),
  })
  const [downloading, setDownloading] = useState(false)
  const [sendingBack, setSendingBack] = useState(false)
  const l = list.data

  const go = (target: CashPaymentTransition['target']) =>
    l && firm && transition.mutate({ firm, week_end: l.week_end, target })

  const downloadPdf = async () => {
    if (!l || !firm) return
    setDownloading(true)
    try {
      const { blob } = await cashApi.paymentListPdf(firm, l.week_end)
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `odeme-listesi-${l.week_end}.pdf`
      a.click()
      URL.revokeObjectURL(url)
    } catch (e) {
      toast({ type: 'error', title: t.pdf, description: toProblem(e).detail ?? undefined })
    } finally {
      setDownloading(false)
    }
  }

  return (
    <div className="flex flex-col gap-4">
      <PageHeader
        title={t.title}
        description={t.description}
        actions={
          <>
            <CashFirmSelect firms={firms.data ?? []} firm={firm} onChange={setFirm} />
            {l && (
              <OptionSelect
                value={l.week_end}
                options={l.week_ends.map((w) => ({ value: w, label: formatDateOnly(w) }))}
                onValueChange={(v) => query.set({ hafta: v })}
                aria-label={t.week}
                className="w-40"
              />
            )}
          </>
        }
      />
      {list.isError ? (
        <ErrorState error={list.error} onRetry={() => void list.refetch()} />
      ) : !l || !firm ? (
        <Loading rows={8} />
      ) : (
        <>
          <div className="bg-surface flex flex-wrap items-center gap-3 rounded-lg border p-3">
            <Badge tone={l.status === 'taslak' ? 'neutral' : 'info'} data-status={l.status}>
              {t.status[l.status]}
            </Badge>
            <span className="text-text-secondary text-xs">
              {formatDateOnly(l.week_start)} – {formatDateOnly(l.week_end)}
            </span>
            {l.submitted_by_name && l.submitted_at && (
              <span className="text-text-secondary text-xs">
                {t.submitted}: {t.by(l.submitted_by_name, formatDateTime(l.submitted_at))}
              </span>
            )}
            {l.approved_by_name && l.approved_at && (
              <span className="text-text-secondary text-xs">
                {t.approved}: {t.by(l.approved_by_name, formatDateTime(l.approved_at))}
              </span>
            )}
            {l.paid_by_name && l.paid_at && (
              <span className="text-text-secondary text-xs">
                {t.paidBy}: {t.by(l.paid_by_name, formatDateTime(l.paid_at))}
              </span>
            )}
            <span className="flex-1" />
            {l.status === 'taslak' && canPlan && (
              <Button loading={transition.isPending} onClick={() => go('onayda')}>
                {t.submit}
              </Button>
            )}
            {/* Onaydan taslağa: cash.plan ya da cash.approve (GECISLER). Onaycı notla geri gönderir;
                onay yetkisi olmayan planlayıcı geri çeker (motor bunu "geri_cek" diye yazar). */}
            {l.status === 'onayda' && canApprove && (
              <Button variant="secondary" onClick={() => setSendingBack(true)}>
                {t.sendBack}
              </Button>
            )}
            {l.status === 'onayda' && canPlan && !canApprove && (
              <Button variant="secondary" onClick={() => go('taslak')}>
                {t.withdraw}
              </Button>
            )}
            {l.status === 'onayda' && canApprove && (
              <Button loading={transition.isPending} onClick={() => go('onaylandi')}>
                {t.approve}
              </Button>
            )}
            {l.status === 'onaylandi' && canApprove && (
              <Button loading={transition.isPending} onClick={() => go('odendi')}>
                {t.paid}
              </Button>
            )}
            <Button variant="secondary" loading={downloading} onClick={() => void downloadPdf()}>
              <Download />
              {t.pdf}
            </Button>
          </div>
          {l.frozen && <Alert tone="info" title={t.frozen} />}
          <PaymentDecisionHistory decisions={l.decisions ?? []} />
          <SendBackDialog
            open={sendingBack}
            firm={firm}
            weekEnd={l.week_end}
            onClose={() => setSendingBack(false)}
          />
          {ibanFilter && t.filterIban[ibanFilter] && (
            <Alert
              tone="info"
              title={t.filterIban[ibanFilter]!}
              onDismiss={() => query.set({ iban: undefined })}
            />
          )}
          {SECTIONS.map((s) => (
            <Section
              key={s}
              firm={firm}
              list={l}
              section={s}
              ibanFilter={ibanFilter}
              canEdit={canPlan}
            />
          ))}
        </>
      )}
    </div>
  )
}
